import type { ID } from '@/backend/types'
import type { OverviewResponse } from '@/backend/types/overview'
import type { Person, PersonRole, ProtocolTypeResponse } from '@/backend/types/protocol-type'

let backend: ZapdosBackend|undefined = undefined

export const useBackend = (): Backend => {
	if (!backend) {
		backend = new ZapdosBackend()
	}
	
	return backend!
}

export interface Backend {
	
	/**
	 * Get the overview of all available protocol types and estate information for an estate ID
	 */
	getOverview(secret: string, estateID: ID, orderID: string | undefined): Promise<OverviewResponse>
	
	/**
	 * Get all information for a specific protocol type for an estate
	 */
	getProtocolType(secret: string, estateID: ID, protocolType: string): Promise<ProtocolTypeResponse>
	
	/**
	 * Disable notifications for a given protocol type
	 */
	stopSendouts(secret: string, estateID: ID, orderID: string|number|null, protocolType: string): Promise<void>
	
	/**
	 * Disable reminder notifications for a given protocol type
	 */
	stopReminders(secret: string, estateID: ID, orderID: string|number|null, protocolType: string): Promise<void>
	
	/**
	 * Submit the protocol to a set of people
	 */
	submit(secret: string, estateID: ID, protocolType: string, payload: Record<string, any>): Promise<void>
}

class ZapdosBackend implements Backend {

	public async getOverview(secret: string, estateID: ID, orderID: string | undefined): Promise<OverviewResponse> {
		let path = `/${estateID}?secret=${secret}`
		
		if (orderID) {
			path += `&orderid=${orderID}`
		}
		
		return this.call('GET', path)
	}
	
	public async getProtocolType(secret: string, estateID: ID, protocolType: string): Promise<ProtocolTypeResponse> {
		return this.mapProtocolType(await this.call('GET', `/${estateID}/${protocolType}?secret=${secret}`))
	}
	
	public async stopSendouts(secret: string, estateID: ID, orderID: string|number|null, protocolType: string): Promise<void> {
		await this.call('POST', `/${estateID}/${protocolType}/stop-automatic-sending`, {
			secret,
			orderid: orderID
		})
	}
	
	public async stopReminders(secret: string, estateID: ID, orderID: string | number | null, protocolType: string): Promise<void> {
		await this.call('POST', `/${estateID}/${protocolType}/stop-reminders`, {
			secret,
			orderid: orderID
		})
	}
	
	public async submit(secret: string, estateID: ID, protocolType: string, payload: Record<string, any>): Promise<any> {
		return await this.call('POST', `/${estateID}/${protocolType}/send-link`, {
			secret,
			...payload,
		})
	}
	
	public mapProtocolType(type: Record<string, any>): ProtocolTypeResponse {
		type.persons = this.addSelectionStatusToPersons(type.persons ?? [])
		type.person_roles = this.addSelectionStatusToPersons(type.person_roles ?? [])
		
		return type as ProtocolTypeResponse
	}
	
	private addSelectionStatusToPersons<T extends Person|PersonRole>(persons: T[]): T[] {
		return persons.map((p: T) => {
			p.selected = {
				phone: false,
				email: false,
				kvadrat: false
			}
			
			return p
		})
	}
	
	public getBaseURL(): string {
		return import.meta.env.ZAPDOS_CRM_BACKEND
	}
	
	/**
	 * Make a request
	 *
	 * @param {string}              method      HTTP method to use
	 * @param {string}              url         URL to call, relative to {@link this.getBaseURL()}
	 * @param {Record<any, any>}    payload     (only if not GET or OPTIONS) Payload to send along
	 *
	 * @returns {Promise<any>}
	 */
	public async call(method: string, url: string, payload?: Record<any, any>): Promise<any> {
		const headers = new Headers({
			"Content-Type": "application/json, charset=utf-8"
		})
		
		const options: RequestInit = {
			method,
			headers
		}
		
		if (payload !== undefined) {
			options.body = JSON.stringify(payload)
		}
		
		const request = new Request(this.getBaseURL() + url, options)
		const response = await fetch(request)
		
		if (!response.ok) {
			throw new Error(`Error while making request: ${response.status} ${response.statusText}`)
		}
		
		return await response.json()
	}
}
